/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import type { TicketListRowGroup } from '../../shared/ticket/TicketListRow'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import type { TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { PromotionWithTargets } from '../domain/promotionTypes';
import {
  formatPromotionBenefit,
  formatPromotionValidity,
  PROMOTION_KIND_LABELS,
  PROMOTION_SCOPE_LABELS,
  promotionStatusLabel,
  promotionStatusTone,
} from '../application/promotionPresentation';

type Props = {
  promo: PromotionWithTargets;
  canEdit: boolean;
  canActivate?: boolean;
  formatRowSubtitle: (promo: PromotionWithTargets) => string;
  onEdit: (promotionId: string) => void;
  onToggleActive: (promo: PromotionWithTargets) => void;
  onDuplicate: (promo: PromotionWithTargets) => void;
  onOpenHistory: (promo: PromotionWithTargets) => void;
  /** Si viene, aparece "Eliminar promoción" en el sheet de más opciones. */
  onDelete?: (promo: PromotionWithTargets) => void | Promise<void>;
  group?: TicketListRowGroup;
};

function mapTone(tone: ReturnType<typeof promotionStatusTone>): 'ok' | 'bad' | 'warn' | 'muted' {
  if (tone === 'success') return 'ok';
  if (tone === 'warning') return 'warn';
  return 'muted';
}

function PromotionRowCardInner({
  promo,
  canEdit,
  canActivate = true,
  formatRowSubtitle,
  onEdit,
  onToggleActive,
  onDuplicate,
  onOpenHistory,
  onDelete,
  group,
}: Props) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const { confirm: confirmDelete, ConfirmModal: DeleteConfirmModal } = useOperationalConfirm();
  const statusLabel = promotionStatusLabel(promo);
  const statusTone = mapTone(promotionStatusTone(promo));
  const detailLine = formatRowSubtitle(promo);
  const benefit = formatPromotionBenefit(promo);
  const validity = formatPromotionValidity(promo);
  const meta = [
    PROMOTION_KIND_LABELS[promo.kind],
    PROMOTION_SCOPE_LABELS[promo.scope],
    detailLine || null,
    validity,
  ]
    .filter(Boolean)
    .join(' · ');

  const code = promo.promoCode?.trim();

  // El sheet se cierra antes de onPress → el confirm monta como modal propio,
  // sin overlay adentro del sheet.
  const handleDelete = useCallback(async () => {
    if (!onDelete) return;
    const ok = await confirmDelete({
      title: 'Eliminar promoción',
      message: `Se borra "${promo.name}". No se puede deshacer.`,
      confirmLabel: 'Eliminar',
      destructive: true,
      iconName: 'trash-outline',
    });
    if (ok) void onDelete(promo);
  }, [confirmDelete, onDelete, promo]);

  const overflowActions = useMemo<TicketMoreAction[]>(() => {
    const items: TicketMoreAction[] = [];
    if (canEdit && canActivate) {
      items.push({
        key: 'toggle-active',
        label: promo.isActive ? 'Pausar promoción' : 'Activar promoción',
        hint: promo.isActive ? 'La pausás. No se borra.' : 'Para que se use en el POS.',
        icon: promo.isActive ? 'pause-outline' : 'play-outline',
        onPress: () => void onToggleActive(promo),
      });
    }
    if (canEdit) {
      items.push({
        key: 'duplicate',
        label: 'Duplicar promoción',
        hint: 'Copia con los mismos datos.',
        icon: 'copy-outline',
        onPress: () => onDuplicate(promo),
      });
    }
    items.push({
      key: 'history',
      label: 'Ver historial',
      hint: 'Quién la tocó y cuándo.',
      icon: 'time-outline',
      onPress: () => onOpenHistory(promo),
      testID: 'promociones.history',
    });
    if (canEdit && onDelete) {
      items.push({
        key: 'delete',
        label: 'Eliminar promoción',
        hint: 'Se borra del listado. No se puede deshacer.',
        icon: 'trash-outline',
        onPress: () => void handleDelete(),
        testID: 'promociones.delete',
      });
    }
    return items;
  }, [canActivate, canEdit, handleDelete, onDelete, onDuplicate, onOpenHistory, onToggleActive, promo]);

  return (
    <>
      <TicketListRow
        title={promo.name}
        meta={meta}
        value={benefit}
        valueChip
        statusLabel={statusLabel}
        statusTone={statusTone}
        chipLabel={code ? `Código ${code}` : null}
        chipTone="default"
        inactive={!promo.isActive}
        group={group}
        onPress={() => onEdit(promo.id)}
        accessibilityLabel={`${promo.name}, ${detailLine}`}
        testID="promociones.row"
        footer={
          canEdit ? (
            <View style={styles.footerRow}>
              {canActivate ? (
                <Switch
                  value={promo.isActive}
                  onValueChange={() => void onToggleActive(promo)}
                  accessibilityLabel={promo.isActive ? 'Pausar promoción' : 'Activar promoción'}
                  testID="promociones.lifecycle-switch"
                />
              ) : null}
              <TouchableOpacity
                style={styles.moreBtn}
                onPress={() => setOverflowOpen(true)}
                accessibilityRole="button"
                accessibilityLabel="Más opciones"
              >
                <Ionicons name="ellipsis-horizontal" size={16} color={ticketColors.ink2} />
              </TouchableOpacity>
            </View>
          ) : null
        }
      />
      <TicketMoreActionsSheet
        visible={overflowOpen}
        title={promo.name}
        actions={overflowActions}
        onClose={() => setOverflowOpen(false)}
      />
      {DeleteConfirmModal}
    </>
  );
}

export const PromotionRowCard = React.memo(PromotionRowCardInner);

const styles = StyleSheet.create({
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  moreBtn: {
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
